import { FC, useState } from 'react';
import { TextField, withStyles, Card, Tooltip, FieldData } from 'vopay';
import styles from './CreateClientAccountPage.module.scss';
import CreateCustomerForm from '@/components/Customer/CreateCustomer/CreateCustomerForm';
import { useTranslation } from 'react-i18next';
import { UtilButton, Title, CardCheckbox } from 'UI';
import { useHistory, useLocation } from 'react-router-dom';
import { useClientData } from '@/providers/ClientData.provider';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import { FormValues, SerializedFormValues } from '@/components/Customer/CreateCustomer/useFormConfig';

const cn = withStyles(styles);

type CreateClientAccountPageProps = {};

type ClientAccountFormValues = {
  identificationNumber: string;
};

type LocationState = {
  showBackButton?: boolean;
};

const CreateClientAccountPage: FC<CreateClientAccountPageProps> = () => {
  const {
    response: { Country: loginUserCountry },
  } = useClientData();

  const history = useHistory();
  const location = useLocation<LocationState>();
  const { state } = location;
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.Create' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [receiveOnly, setReceiveOnly] = useState(false);
  const { createClientAccount } = useClientAccounts();

  const moreInfoConfig: FieldData<FormValues & ClientAccountFormValues>[] = [
    {
      label: () => t('identificationNumber'),
      fieldName: 'identificationNumber',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <TextField
            {...getFormikData<ClientAccountFormValues['identificationNumber']>({})}
            variant="outlineShadow"
            preset="positiveInteger"
          />
        );
      },
    },
  ];

  async function handleCreateAccount(values: SerializedFormValues & ClientAccountFormValues) {
    try {
      const res = await createClientAccount(
        {
          FirstName: values.firstName,
          LastName: values.lastName,
          EmailAddress: values.email,
          PhoneNumber: values.phone,
          BusinessName: values.businessName,
          ClientAccountID: values.accountID,
          Label: values.label,
          Address1: values.address1,
          City: values.city,
          Province: values.province,
          PostalCode: values.postalCode,
          Country: values.country,
          Currency: loginUserCountry === 'CA' ? 'CAD' : 'USD',
          IdentificationNumber: values.identificationNumber,
          FlinksAccountID: values.flinksAccountID,
          FlinksLoginID: values.flinksLoginID,
          PlaidProcessorToken: values.plaidProcessorToken,
        },
        receiveOnly,
      );

      return res.clientAccountID;
    } catch (err) {
      throw err;
    }
  }

  const showBackButton = state?.showBackButton;

  return (
    <div className={cn('createClientAccountPage')}>
      {showBackButton && (
        <UtilButton
          iconLeft="chevron_left"
          size="large"
          onClick={() => {
            if (history.length > 1) {
              history.goBack();
            } else {
              history.replace('/client-accounts');
            }
          }}
        >
          {history.length > 1 ? tGlobal('Buttons.back') : t('clientAccountTitle')}
        </UtilButton>
      )}
      <Title className={cn('title')}>{t('pageTitle', { label: t('clientAccount') })}</Title>

      <div className={cn('form')}>
        <div className={cn('left')}>
          <CreateCustomerForm
            moreInfoConfig={moreInfoConfig}
            moreInfoInitialValues={{
              identificationNumber: '',
            }}
            customerLabel={t('clientAccount')}
            createAccount={handleCreateAccount}
            onComplete={(accountID, search) => {
              history.push({
                pathname: `/client-accounts/${accountID}`,
                search: search ? new URLSearchParams(search).toString() : undefined,
              });
            }}
          />
        </div>

        <div className={cn('right')}>
          <Card className={cn('clientAccountSettings')}>
            <Title level="5">{t('clientAccountSettings')}</Title>

            <CardCheckbox
              name="receiveOnly"
              label={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <div>{t('receiveOnly')}</div>
                  <Tooltip icon="info" position="top" information={t('receiveOnlyTooltip')} />
                </div>
              }
              checked={receiveOnly}
              indicator="switch"
              onChange={(e) => setReceiveOnly(e.target.checked)}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CreateClientAccountPage;
